import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MaterialTable from '@material-table/core'
import { Box, Button, Typography } from '@material-ui/core'
import { PartyManagementController } from 'controllers/System/PartyManagementController'
import { TenantController } from 'controllers/System/TenantController'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ManagementPartyDto,
  RhTenantInfo,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { PartiesModal } from './components/PartiesModal'

export type PartyItem = {
  hasSelfSevicePortal?: boolean
} & ManagementPartyDto

const emptyPartyItem: PartyItem = {
  id: '',
  name: '',
  coCKey: '',
  hasSubscription: false,
  hasSelfSevicePortal: false,
  currencyCode: 'EUR'
}

export const PartiesPage = () => {
  const controller = new PartyManagementController()
  const tenantController = new TenantController()

  const { t } = useTranslation()
  const { openDialog } = useDialogManager()

  const [data, setData] = useState<PartyItem[]>([])
  // eslint-disable-next-line
  const [tenants, setTenants] = useState<Map<string, RhTenantInfo>>(new Map())
  const [openPartyModal, setOpenPartyModal] = useState(false)
  const [isEditMode, setEditMode] = useState(false)

  const [currentPartData, setCurrentPartData] =
    useState<PartyItem>(emptyPartyItem)

  const loadData = async () => {
    try {
      const [result, resultTenants] = await Promise.all([
        controller.GetAll(),
        tenantController.GetAll(),
      ])

      const tenantsByPartyId = resultTenants.reduce(
        (tenantMap, t) => tenantMap.set(t.partyId, t),
        new Map<string, RhTenantInfo>()
      )

      const partItems = result.map((party) =>
        Object.assign(party, {
          hasSelfSevicePortal: tenantsByPartyId.has(party.id),
        })
      )

      setData(partItems)
      setTenants(tenantsByPartyId)
    } catch (err) {
      ShowException('System Management > Load Parties', err)
    }
  }

  const handleTenantTreatments = async (
    party: ManagementPartyDto,
    hasSelfSevicePortal: boolean
  ) => {
    const tenantData = {
      identifier: party.id,
      name: party.name,
      partyId: party.id,
    }

    if (hasSelfSevicePortal) {
      return !tenants.has(party.id)
        ? await tenantController.Add(tenantData)
        : await tenantController.Update({
            ...tenantData,
            id: tenants.get(party.id).id,
          })
    }

    if (tenants.has(party.id)) {
      const result = tenantController.Delete(tenants.get(party.id).id)
      tenants.delete(party.id)
      return result
    }
  }

  // eslint-disable-next-line
  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openPartyModalForm = (party: PartyItem = {}, editMode = false) => {
    setCurrentPartData(Object.assign({}, emptyPartyItem, party))
    setEditMode(editMode)
    setOpenPartyModal(true)
  }

  const handleSaveParty = async (party: PartyItem) => {
    try {
      const result: ManagementPartyDto = isEditMode
        ? await controller.Update(party)
        : await controller.Add(party)

      await handleTenantTreatments(result, party.hasSelfSevicePortal)
      await loadData()

      return result
    } catch (err) {
      ShowException('System Management > Save Party', err)
      return false
    }
  }

  const handleDeleteParty = async (party: PartyItem) => {
    try {
      await controller.Delete(party.id)

      if (tenants.has(party.id)) {
        await tenantController.Delete(tenants.get(party.id).id)
        tenants.delete(party.id)
      }

      await loadData()
      return true
    } catch (err) {
      ShowException('System Management > Delete Party', err)
      return false
    }
  }

  const openDeletePartyModalDialog = (party: PartyItem) => {
    openDialog('ConfirmationDialog', {
      title: t(
        'system:delete-current-party',
        'Are you sure you to delete party "{{name}}"?',
        {
          name: party.name,
        }
      ),
      onConfirm: () => handleDeleteParty(party),
    })
  }

  return (
    <>
      {openPartyModal && (
        <PartiesModal
          open={openPartyModal}
          data={currentPartData}
          onClose={() => setOpenPartyModal(false)}
          onConfirm={handleSaveParty}
        />
      )}

      <MaterialTable
        title={t('system:parties', 'Parties')}
        data={data}
        options={{
          paging: false,
          search: true,
          showTitle: true,
          defaultOrderByCollection: [],
          headerStyle: {
            position: 'sticky',
            top: 0,
            fontSize: '1em',
            fontWeight: 'bold',
          },
        }}
        columns={[
          {
            title: 'id',
            field: 'id',
          },
          {
            title: 'name',
            field: 'name',
          },
          {
            title: 'coCKey',
            field: 'coCKey',
            width: '80px',
          },
          {
            title: 'hasSubscription',
            field: 'hasSubscription',
            width: '80px',
          },
          {
            title: 'currencyCode',
            field: 'currencyCode',
            width: '80px',
          },
          {
            title: 'hasSelfSevicePortal',
            field: 'hasSelfSevicePortal',
            width: '80px',
          },
          {
            title: t('common:actions'),
            field: 'actions',
            width: '120px',
            searchable: false,
            sorting: false,
            render: (resource) => {
              return (
                <Box display="flex">
                  <Button
                    onClick={() => {
                      openPartyModalForm(resource, true)
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      component="span"
                      className="icon"
                    >
                      <FontAwesomeIcon icon={faEdit as IconProp} />
                    </Typography>
                  </Button>
                  <Button
                    onClick={() => {
                      openDeletePartyModalDialog(resource)
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      component="span"
                      className="icon"
                    >
                      <FontAwesomeIcon icon={faTrash as IconProp} />
                    </Typography>
                  </Button>
                </Box>
              )
            },
          },
        ]}
        actions={[
          {
            icon: 'add',
            isFreeAction: true,
            tooltip: t('system:add-new-party', 'add new party'),
            onClick: () => openPartyModalForm(),
          },
        ]}
      />
    </>
  )
}
